import aiChatService from '../services/aiChatService';
import processRawText from '../utils/messageParser';
import { findFixtureByUserText, findReplyChunks } from '../utils/testModeFixtures';
import debugLogger from '../utils/debugLogger';

/**
 * Interval (ms) between simulated text_delta events in test-mode — slow enough
 * to let a human see the stream progress, fast enough not to feel broken.
 */
const TEST_MODE_CHUNK_DELAY_MS = 40;

const buildUserMessage = (text) => ({
  role: 'user',
  text,
  entityRefs: [],
  isStreaming: false,
});

/** Best-effort conversation_id when JSON.parse fails on SSE result (e.g. framing edge cases). */
const parseResultIdsFallback = (text) => {
  const mC = text.match(/"conversation_id"\s*:\s*"([0-9a-f-]{36})"/i);
  if (!mC?.[1]) return null;
  return { conversation_id: mC[1] };
};

const buildAiMessage = () => ({
  role: 'ai',
  rawText: '',
  reasoningSegments: [],
  text: '',
  suggestions: [],
  interactions: [],
  cards: [],
  entities: [],
  entityRefs: [],
  /** Shown until the first `status` / `tool_status` SSE or cleared when answer text streams. */
  streamStatus: 'thinking',
  isReasoning: false,
  isStreaming: true,
  isWaiting: true,
});

/** Same SSE handling for send and confirm (both POST …/message/stream). */
const handleChatStreamEvent = (commit) => (event) => {
  commit('markFirstEventReceived');
  if (event.type === 'error') {
    commit('setError', (event.text && event.text.trim()) || 'Something went wrong. Please try again.');
  }
  if (event.type === 'delta_reasoning') commit('appendReasoningDelta', event.text);
  if (event.type === 'tool_status' || event.type === 'status') commit('appendStatus', event.text);
  if (event.type === 'text_delta') commit('appendTextDelta', event.text);
  if (event.type === 'conversation_id') {
    commit('setConversationId', event.text.trim());
  }
  if (event.type === 'result') {
    try {
      commit('setResult', JSON.parse(event.text));
    } catch {
      const partial = parseResultIdsFallback(event.text);
      if (partial) commit('setResult', partial);
    }
  }
};

const state = {
  panelOpen: false,
  messages: [],
  conversationId: null,
  isStreaming: false,
  error: null,
  testMode: false,
};

const getters = {
  panelOpen: (s) => s.panelOpen,
  messages: (s) => s.messages,
  isStreaming: (s) => s.isStreaming,
  error: (s) => s.error,
  testMode: (s) => s.testMode,
};

const mutations = {
  openPanel: (state) => { state.panelOpen = true; },
  closePanel: (state) => { state.panelOpen = false; },
  appendMessage: (state, message) => { state.messages = [...state.messages, message]; },
  setStreaming: (state, value) => { state.isStreaming = value; },
  setError: (state, message) => { state.error = message; },
  clearError: (state) => { state.error = null; },
  appendReasoningDelta: (state, delta) => {
    const messages = [...state.messages];
    const last = { ...messages[messages.length - 1] };
    const segs = [...last.reasoningSegments];
    const lastSeg = segs[segs.length - 1];
    if (lastSeg && lastSeg.type === 'text') {
      segs[segs.length - 1] = { ...lastSeg, text: lastSeg.text + delta };
    } else {
      segs.push({ type: 'text', text: delta });
    }
    messages[messages.length - 1] = { ...last, reasoningSegments: segs, isReasoning: true };
    state.messages = messages;
  },
  appendStatus: (state, statusText) => {
    const trimmed = (statusText && String(statusText).trim()) || '';
    const messages = [...state.messages];
    const last = { ...messages[messages.length - 1] };
    messages[messages.length - 1] = {
      ...last,
      streamStatus: trimmed || null,
    };
    state.messages = messages;
  },
  appendTextDelta: (state, delta) => {
    const messages = [...state.messages];
    const last = { ...messages[messages.length - 1] };
    const rawText = (last.isReasoning ? '' : last.rawText) + delta;
    const { text, afterText, suggestions, cards, entities, interactions } = processRawText(rawText);
    messages[messages.length - 1] = {
      ...last,
      rawText,
      text,
      afterText,
      suggestions,
      cards,
      entities,
      interactions,
      isReasoning: false,
      streamStatus: null,
    };
    state.messages = messages;
  },
  finalizeLastMessage: (state) => {
    const messages = [...state.messages];
    messages[messages.length - 1] = { ...messages[messages.length - 1], isStreaming: false, isWaiting: false, streamStatus: null };
    state.messages = messages;
  },
  markFirstEventReceived: (state) => {
    const messages = [...state.messages];
    const last = messages[messages.length - 1];
    if (!last || !last.isWaiting) return;
    messages[messages.length - 1] = { ...last, isWaiting: false };
    state.messages = messages;
  },
  setResult: (state, result) => {
    state.conversationId = result.conversation_id || state.conversationId;
  },
  setConversationId: (state, id) => {
    if (id) state.conversationId = id;
  },
  setTestMode: (state, value) => { state.testMode = !!value; },
  markConfirmActioned: (state, conversationId) => {
    state.messages = state.messages.map((msg) => (
      msg.role === 'confirm' && msg.conversationId === conversationId
        ? { ...msg, isActioned: true }
        : msg
    ));
  },
  resetConversation: (state) => {
    state.messages = [];
    state.conversationId = null;
    state.isStreaming = false;
    state.error = null;
  },
};

/**
 * Replay canned text chunks through the normal stream handler, so the parser
 * and UI exercise exactly the same code path as a real SSE reply. Used only
 * when test-mode is active.
 *
 * We also `console.debug` every event we dispatch and the final parsed state,
 * so you can follow the "stream" and the post-parse Vuex state in DevTools.
 * Filter by `[AiChat test-mode]` in the Console to see the whole flow.
 */
const simulateStream = (commit, chunks, { label, getFinalMessage }) => new Promise((resolve) => {
  const onEvent = handleChatStreamEvent(commit);
  const tag = `[AiChat test-mode] ${label}`;
  const rawReply = chunks.join('');
  debugLogger.info(
    `%c${tag}%c raw LLM-like reply:\n%c${rawReply}`,
    'background:#4a90e2;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
    'color:inherit',
    'color:#888;white-space:pre-wrap',
  );
  debugLogger.groupCollapsed(`${tag} (per-chunk detail)`);
  debugLogger.debug(`${chunks.length} chunk(s), ${TEST_MODE_CHUNK_DELAY_MS}ms apart`);
  let i = 0;
  const next = () => {
    if (i >= chunks.length) {
      try {
        const finalMsg = getFinalMessage?.();
        if (finalMsg) {
          debugLogger.debug('final message state:', {
            text: finalMsg.text,
            entities: finalMsg.entities,
            interactions: finalMsg.interactions,
            suggestions: finalMsg.suggestions,
          });
        }
      } finally {
        debugLogger.groupEnd();
      }
      resolve();
      return;
    }
    const chunk = chunks[i];
    debugLogger.debug(`chunk ${i + 1}/${chunks.length} →`, chunk);
    onEvent({ type: 'text_delta', text: chunk });
    i += 1;
    setTimeout(next, TEST_MODE_CHUNK_DELAY_MS);
  };
  next();
});

const actions = {
  openPanel: ({ commit }) => commit('openPanel'),
  closePanel: ({ commit }) => commit('closePanel'),
  resetConversation: ({ commit }) => commit('resetConversation'),
  toggleTestMode: ({ commit, state: s }) => commit('setTestMode', !s.testMode),

  async sendMessage({ commit, state: s }, { text, context }) {
    commit('clearError');
    commit('appendMessage', buildUserMessage(text));
    commit('appendMessage', buildAiMessage());
    commit('setStreaming', true);

    const fixture = s.testMode ? findFixtureByUserText(text) : null;
    if (fixture) {
      try {
        await simulateStream(commit, fixture.chunks, {
          label: `send "${text}"`,
          getFinalMessage: () => s.messages[s.messages.length - 1],
        });
      } finally {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
      return;
    }

    const payload = {
      message: text,
      user_view_context: context,
    };
    if (s.conversationId) payload.conversation_id = s.conversationId;

    try {
      await aiChatService.streamMessage(payload, handleChatStreamEvent(commit));
    } catch (err) {
      commit('setError', 'Something went wrong. Please try again.');
    } finally {
      commit('finalizeLastMessage');
      commit('setStreaming', false);
    }
  },

  /**
   * Reply to a HITL interaction streamed by the assistant.
   *
   * Accepts `{ uid?, confirm }`. Sends `{ interaction: { uid?, confirm }, conversation_id }`.
   * `uid` is present for code-driven (parked) slots; absent for LLM-driven confirms.
   * `confirm` is a string: "yes" executes, anything else is treated as a cancel reason.
   */
  async confirmAction({ commit, state: s }, payload) {
    const { uid, confirm, userText } = payload || {};
    commit('clearError');
    if (!s.conversationId) {
      commit('setError', 'Could not process your confirmation. Wait for the reply to finish, then try again.');
      return;
    }
    if (!confirm) {
      commit('setError', 'Could not process your confirmation. Please try again.');
      return;
    }
    if (userText) commit('appendMessage', buildUserMessage(userText));
    commit('appendMessage', buildAiMessage());
    commit('setStreaming', true);

    // eslint-disable-next-line no-underscore-dangle
    const replyChunks = s.testMode ? findReplyChunks(null, confirm) : null;
    if (replyChunks) {
      try {
        await simulateStream(commit, replyChunks, {
          label: `confirm → ${confirm}`,
          getFinalMessage: () => s.messages[s.messages.length - 1],
        });
      } finally {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
      return;
    }

    const interaction = { confirm };
    if (uid) interaction.uid = uid;

    const streamPayload = {
      conversation_id: s.conversationId,
      interaction,
    };

    try {
      await aiChatService.streamMessage(streamPayload, handleChatStreamEvent(commit));
    } catch (err) {
      commit('setError', 'Something went wrong. Please try again.');
    } finally {
      commit('finalizeLastMessage');
      commit('setStreaming', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
